import React, { useState, useRef, Component } from 'react';
import { SEOHead } from '../../components/SEOHead';
import { ScrollReveal } from '../../components/ScrollReveal';
import { CheckCircleIcon, CopyIcon, AlertCircleIcon, Wand2Icon, UploadIcon, LinkIcon, XIcon, SendIcon, LoaderIcon, LockIcon } from 'lucide-react';
// Replace this with your actual GHL webhook URL
const GHL_WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/JLycxFbUVQFY3z4zPpCS/webhook-trigger/1388343a-44e6-4563-8cd2-2acd1a8562be';
// Define the social networks
const SOCIAL_NETWORKS = [{
  id: 'linkedin',
  name: 'LinkedIn',
  icon: 'https://img.icons8.com/color/48/000000/linkedin.png',
  defaultUrl: 'https://linkedin.com/'
}, {
  id: 'yelp',
  name: 'Yelp',
  icon: 'https://img.icons8.com/color/48/000000/yelp.png',
  defaultUrl: ''
}, {
  id: 'google',
  name: 'Google Business',
  icon: 'https://img.icons8.com/color/48/000000/google-logo.png',
  defaultUrl: ''
}, {
  id: 'instagram',
  name: 'Instagram',
  icon: 'https://img.icons8.com/color/48/000000/instagram-new.png',
  defaultUrl: 'https://instagram.com/'
}, {
  id: 'facebook',
  name: 'Facebook',
  icon: 'https://img.icons8.com/color/48/000000/facebook-new.png',
  defaultUrl: ''
}, {
  id: 'twitter',
  name: 'Twitter / X',
  icon: 'https://img.icons8.com/color/48/000000/twitter--v1.png',
  defaultUrl: ''
}, {
  id: 'youtube',
  name: 'YouTube',
  icon: 'https://img.icons8.com/color/48/000000/youtube-play.png',
  defaultUrl: ''
}, {
  id: 'tiktok',
  name: 'TikTok',
  icon: 'https://img.icons8.com/color/48/000000/tiktok--v1.png',
  defaultUrl: ''
}, {
  id: 'pinterest',
  name: 'Pinterest',
  icon: 'https://img.icons8.com/color/48/000000/pinterest--v1.png',
  defaultUrl: ''
}];
export function GmailSignatureGeneratorPage() {
  // State for all inputs
  const [themeColor, setThemeColor] = useState('#10B981'); // viridia green
  const [logoSize, setLogoSize] = useState(130);
  const [logoUrl, setLogoUrl] = useState('https://images.unsplash.com/photo-1614680376573-3e4e1ef4142a?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80');
  const [personalInfo, setPersonalInfo] = useState({
    name: 'Jane Doe',
    role: 'Marketing Director',
    phone: '(555) 123-4567',
    email: 'hello@yourcompany.com',
    location: 'Phoenix, AZ',
    website: 'www.yourcompany.com'
  });
  // Initialize social links state
  const initialSocials = SOCIAL_NETWORKS.reduce((acc, net) => {
    acc[net.id] = net.defaultUrl;
    return acc;
  }, {} as Record<string, string>);
  const [socialLinks, setSocialLinks] = useState(initialSocials);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const signatureRef = useRef<HTMLDivElement>(null);
  // Opt-in gate state
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showOptIn, setShowOptIn] = useState(false);
  const [optInData, setOptInData] = useState({
    name: '',
    email: '',
    businessName: ''
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [optInSubmitting, setOptInSubmitting] = useState(false);
  const [optInError, setOptInError] = useState('');
  // Handlers
  const handleInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPersonalInfo({
      ...personalInfo,
      [e.target.name]: e.target.value
    });
  };
  const handleSocialChange = (id: string, value: string) => {
    setSocialLinks({
      ...socialLinks,
      [id]: value
    });
  };
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = event => {
        if (event.target?.result) {
          setLogoUrl(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const copySignature = () => {
    if (!signatureRef.current) return;
    try {
      const range = document.createRange();
      range.selectNode(signatureRef.current);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        const successful = document.execCommand('copy');
        if (successful) {
          showToast('Success! Signature copied to clipboard.', 'success');
        } else {
          showToast('Failed to copy. Please select it manually.', 'error');
        }
        selection.removeAllRanges();
      }
    } catch (err) {
      showToast('Your browser does not support auto-copying. Please select it manually.', 'error');
    }
  };
  const handleCopyClick = () => {
    if (isUnlocked) {
      copySignature();
    } else {
      setShowOptIn(true);
    }
  };
  const handleOptInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOptInSubmitting(true);
    setOptInError('');
    try {
      // Use no-cors mode to avoid CORS blocking on GHL webhook
      // With no-cors, we can't read the response, but the POST still reaches GHL
      await fetch(GHL_WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain'
        },
        body: JSON.stringify({
          firstName: optInData.name.split(' ')[0],
          lastName: optInData.name.split(' ').slice(1).join(' '),
          email: optInData.email,
          companyName: optInData.businessName,
          source: 'Gmail Signature Generator'
        })
      });
      setIsUnlocked(true);
      setShowOptIn(false);
      // Auto-copy after unlocking
      setTimeout(() => copySignature(), 300);
    } catch {
      // Even if the fetch appears to fail, the data likely reached GHL
      // Unlock anyway so the user isn't stuck
      setIsUnlocked(true);
      setShowOptIn(false);
      setTimeout(() => copySignature(), 300);
    } finally {
      setOptInSubmitting(false);
    }
  };
  // Derived values for the signature
  const hexNoHash = themeColor.replace('#', '');
  const formattedPhone = personalInfo.phone.replace(/[^0-9+]/g, '');
  const formattedWebsite = personalInfo.website.startsWith('http') ? personalInfo.website : `https://${personalInfo.website}`;
  const encodedLocation = encodeURIComponent(personalInfo.location);
  return <div className="bg-viridia-black min-h-screen pt-24 pb-20" data-id="element-2388">
      <SEOHead title="Free Gmail Signature Generator | Viridia Analytics" description="Create a professional, mobile-friendly email signature for Gmail. Customize colors, add your logo, and include social links. 100% free tool." canonical="/tools/gmail-signature-generator" data-id="element-2389" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2390">
        <ScrollReveal data-id="element-2391">
          <header className="mb-12 text-center max-w-3xl mx-auto" data-id="element-2392">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-4 border border-viridia/20" data-id="element-2393">
              FREE TOOL
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2394">
              Gmail Signature Generator
            </h1>
            <p className="text-gray-400 text-lg" data-id="element-2395">
              Customize your details, then copy and paste directly into your
              Gmail settings. No coding required.
            </p>
          </header>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-2396">
          {/* LEFT COLUMN: CONTROLS */}
          <div className="lg:col-span-5 xl:col-span-4 space-y-6" data-id="element-2397">
            {/* 1. Branding & Logo */}
            <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800" data-id="element-2398">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800" data-id="element-2399">
                1. Branding & Logo
              </h2>
              <div className="space-y-5" data-id="element-2400">
                <div data-id="element-2401">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2402">
                    Brand Color
                  </label>
                  <div className="flex items-center gap-3" data-id="element-2403">
                    <input type="color" value={themeColor} onChange={e => setThemeColor(e.target.value)} className="h-10 w-20 rounded cursor-pointer border-0 bg-transparent p-0" data-id="element-2404" />
                    <span className="text-xs text-gray-500" data-id="element-2405">
                      Colors your name & icons
                    </span>
                  </div>
                </div>

                <div data-id="element-2406">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2407">
                    Logo Size:{' '}
                    <span className="font-bold text-viridia" data-id="element-2408">{logoSize}</span>px
                  </label>
                  <input type="range" min="60" max="250" value={logoSize} onChange={e => setLogoSize(Number(e.target.value))} className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-viridia" data-id="element-2409" />
                </div>

                <div className="pt-3 border-t border-gray-800" data-id="element-2410">
                  <label className="block text-sm font-medium text-gray-300 mb-3" data-id="element-2411">
                    Logo Image
                  </label>
                  <div className="space-y-3" data-id="element-2412">
                    <div className="relative" data-id="element-2413">
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" data-id="element-2414" />
                      <label htmlFor="logo-upload" className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-viridia bg-viridia/10 border border-viridia/20 rounded-md cursor-pointer hover:bg-viridia/20 transition-colors" data-id="element-2415">
                        <UploadIcon className="w-4 h-4 mr-2" data-id="element-2416" />
                        Upload Image
                      </label>
                    </div>
                    <div className="text-center text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-2417">
                      or
                    </div>
                    <div className="relative" data-id="element-2418">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2419">
                        <LinkIcon className="h-4 w-4 text-gray-500" data-id="element-2420" />
                      </div>
                      <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} className="w-full pl-10 pr-3 py-2 bg-viridia-black border border-gray-700 rounded-md focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia text-sm text-white" placeholder="Paste image URL here..." data-id="element-2421" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Personal Info */}
            <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800" data-id="element-2422">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800" data-id="element-2423">
                2. Personal Info
              </h2>
              <div className="space-y-4" data-id="element-2424">
                {Object.entries(personalInfo).map(([key, value]) => <div key={key} data-id="element-2425">
                    <label className="block text-sm font-medium text-gray-300 mb-1 capitalize" data-id="element-2426">
                      {key}
                    </label>
                    <input type={key === 'email' ? 'email' : 'text'} name={key} value={value} onChange={handleInfoChange} className="w-full px-3 py-2 bg-viridia-black border border-gray-700 rounded-md focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia text-sm text-white" data-id="element-2427" />
                  </div>)}
              </div>
            </div>

            {/* 3. Social Links */}
            <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800" data-id="element-2428">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800" data-id="element-2429">
                3. Social Links
              </h2>
              <div className="bg-viridia-black/50 p-3 rounded border border-gray-800 mb-4 flex items-start gap-2" data-id="element-2430">
                <Wand2Icon className="w-4 h-4 text-viridia flex-shrink-0 mt-0.5" data-id="element-2431" />
                <p className="text-xs text-gray-400" data-id="element-2432">
                  <strong className="text-gray-300" data-id="element-2433">Smart Hide:</strong> Leave a
                  link blank to instantly remove its icon from the signature.
                </p>
              </div>
              <div className="space-y-4" data-id="element-2434">
                {SOCIAL_NETWORKS.map(net => <div key={net.id} data-id="element-2435">
                    <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-2436">
                      {net.name} URL
                    </label>
                    <input type="text" value={socialLinks[net.id]} onChange={e => handleSocialChange(net.id, e.target.value)} className="w-full px-3 py-2 bg-viridia-black border border-gray-700 rounded-md focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia text-sm text-white" placeholder={`https://${net.id}.com/...`} data-id="element-2437" />
                  </div>)}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: PREVIEW & INSTRUCTIONS */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-6" data-id="element-2438">
            {/* Live Preview */}
            <div className="bg-viridia-surface rounded-xl p-6 md:p-8 border border-gray-800 sticky top-24" data-id="element-2439">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b border-gray-800 gap-4" data-id="element-2440">
                <h2 className="text-xl font-bold text-white" data-id="element-2441">Live Preview</h2>
                <button onClick={handleCopyClick} className="bg-viridia hover:bg-viridia-dark text-viridia-black font-bold py-2.5 px-6 rounded-lg shadow-lg transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-2442">
                  {isUnlocked ? <>
                      <CopyIcon className="w-4 h-4" data-id="element-2443" /> Copy Signature
                    </> : <>
                      <LockIcon className="w-4 h-4" data-id="element-2444" /> Get Your Signature
                    </>}
                </button>
              </div>

              {/* The actual signature container (white background for email context) */}
              <div className="bg-white p-6 md:p-8 rounded-lg border border-gray-200 overflow-x-auto min-h-[250px]" data-id="element-2445">
                {/* ======================================================= */}
                {/* START OF EMAIL SIGNATURE HTML (Strict Table Layout)     */}
                {/* ======================================================= */}
                <div ref={signatureRef} data-id="element-2446">
                  <table cellPadding="0" cellSpacing="0" border={0} style={{
                  background: 'none',
                  border: 'solid 0px transparent',
                  margin: 0,
                  padding: 0,
                  fontFamily: 'Arial, Helvetica, sans-serif'
                }} data-id="element-2447">
                    <tbody data-id="element-2448">
                      <tr data-id="element-2449">
                        {/* LOGO COLUMN */}
                        <td valign="middle" style={{
                        paddingRight: '20px',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }} data-id="element-2450">
                          <a href={formattedWebsite} target="_blank" rel="noopener noreferrer" style={{
                          textDecoration: 'none'
                        }} data-id="element-2451">
                            <img src={logoUrl || 'https://images.unsplash.com/photo-1614680376573-3e4e1ef4142a?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'} width={logoSize} alt="Company Logo" style={{
                            border: 'none',
                            maxWidth: `${logoSize}px`,
                            display: 'block',
                            borderRadius: '4px'
                          }} data-id="element-2452" />
                          </a>
                        </td>

                        {/* DIVIDER LINE */}
                        <td width="2" style={{
                        backgroundColor: '#8c8c8c',
                        width: '2px',
                        padding: 0
                      }} data-id="element-2453"></td>

                        {/* INFO COLUMN */}
                        <td valign="middle" style={{
                        paddingLeft: '20px',
                        verticalAlign: 'middle'
                      }} data-id="element-2454">
                          {/* Header Text */}
                          <div style={{
                          marginBottom: '8px'
                        }} data-id="element-2455">
                            <span style={{
                            fontFamily: 'Arial, Helvetica, sans-serif',
                            fontSize: '17px',
                            fontWeight: 'bold',
                            color: themeColor,
                            letterSpacing: '2px',
                            textTransform: 'uppercase'
                          }} data-id="element-2456">
                              {personalInfo.name || ' '}
                            </span>
                            <br data-id="element-2457" />
                            <span style={{
                            fontFamily: 'Arial, Helvetica, sans-serif',
                            fontSize: '13px',
                            fontStyle: 'italic',
                            color: '#666666',
                            letterSpacing: '0.5px'
                          }} data-id="element-2458">
                              {personalInfo.role || ' '}
                            </span>
                          </div>

                          {/* Contact Links Table */}
                          <table cellPadding="0" cellSpacing="0" border={0} style={{
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontSize: '12px',
                          color: '#444444',
                          lineHeight: '20px'
                        }} data-id="element-2459">
                            <tbody data-id="element-2460">
                              {/* Phone */}
                              {personalInfo.phone && <tr data-id="element-2461">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '3px'
                              }} data-id="element-2462">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/phone--v1.png`} alt="Phone" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2463" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '3px'
                              }} data-id="element-2464">
                                    <a href={`tel:${formattedPhone}`} style={{
                                  color: '#444444',
                                  textDecoration: 'none',
                                  fontWeight: 600
                                }} data-id="element-2465">
                                      {personalInfo.phone}
                                    </a>
                                  </td>
                                </tr>}

                              {/* Email */}
                              {personalInfo.email && <tr data-id="element-2466">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '3px'
                              }} data-id="element-2467">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/mail.png`} alt="Email" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2468" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '3px'
                              }} data-id="element-2469">
                                    <a href={`mailto:${personalInfo.email}`} style={{
                                  color: '#444444',
                                  textDecoration: 'none',
                                  fontWeight: 600
                                }} data-id="element-2470">
                                      {personalInfo.email}
                                    </a>
                                  </td>
                                </tr>}

                              {/* Location */}
                              {personalInfo.location && <tr data-id="element-2471">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '3px'
                              }} data-id="element-2472">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/marker.png`} alt="Location" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2473" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '3px',
                                fontWeight: 600
                              }} data-id="element-2474">
                                    <a href={`https://maps.google.com/?q=${encodedLocation}`} target="_blank" rel="noopener noreferrer" style={{
                                  color: '#444444',
                                  textDecoration: 'none'
                                }} data-id="element-2475">
                                      {personalInfo.location}
                                    </a>
                                  </td>
                                </tr>}

                              {/* Website */}
                              {personalInfo.website && <tr data-id="element-2476">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '8px'
                              }} data-id="element-2477">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/domain.png`} alt="Website" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2478" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '8px'
                              }} data-id="element-2479">
                                    <a href={formattedWebsite} target="_blank" rel="noopener noreferrer" style={{
                                  color: '#0000ee',
                                  textDecoration: 'underline',
                                  fontWeight: 600
                                }} data-id="element-2480">
                                      {personalInfo.website}
                                    </a>
                                  </td>
                                </tr>}
                            </tbody>
                          </table>

                          {/* Social Media Icons Table */}
                          <table cellPadding="0" cellSpacing="0" border={0} style={{
                          marginTop: '4px'
                        }} data-id="element-2481">
                            <tbody data-id="element-2482">
                              <tr data-id="element-2483">
                                {SOCIAL_NETWORKS.map(net => {
                                const url = socialLinks[net.id];
                                if (!url || url.trim() === '') return null;
                                return <td key={net.id} style={{
                                  paddingRight: '12px'
                                }} data-id="element-2484">
                                      <a href={url} target="_blank" rel="noopener noreferrer" data-id="element-2485">
                                        <img src={net.icon} alt={net.name} width="22" style={{
                                      display: 'block',
                                      border: 'none'
                                    }} data-id="element-2486" />
                                      </a>
                                    </td>;
                              })}
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* ======================================================= */}
                {/* END OF EMAIL SIGNATURE HTML                             */}
                {/* ======================================================= */}
              </div>

              {/* Instructions */}
              <div className="mt-8 bg-viridia-black/50 border-l-4 border-viridia p-5 rounded-r-lg" data-id="element-2487">
                <h3 className="font-bold text-white mb-3" data-id="element-2488">
                  How to install in Gmail:
                </h3>
                <ol className="list-decimal ml-5 space-y-2 text-gray-400 text-sm" data-id="element-2489">
                  <li data-id="element-2490">
                    Ensure all your links are correct in the inputs on the left.
                  </li>
                  <li data-id="element-2491">
                    Click the{' '}
                    <strong className="text-gray-300" data-id="element-2492">"Copy Signature"</strong>{' '}
                    button above.
                  </li>
                  <li data-id="element-2493">
                    Open Gmail, click the{' '}
                    <strong className="text-gray-300" data-id="element-2494">
                      Settings (Gear) icon
                    </strong>
                    , and click{' '}
                    <strong className="text-gray-300" data-id="element-2495">
                      "See all settings"
                    </strong>
                    .
                  </li>
                  <li data-id="element-2496">
                    Scroll down to the{' '}
                    <strong className="text-gray-300" data-id="element-2497">Signature</strong>{' '}
                    section. Create a new one or edit an existing one.
                  </li>
                  <li data-id="element-2498">
                    Click into the text box and press{' '}
                    <strong className="text-gray-300" data-id="element-2499">Ctrl+V</strong> (Windows)
                    or <strong className="text-gray-300" data-id="element-2500">Cmd+V</strong> (Mac) to
                    paste.
                  </li>
                  <li data-id="element-2501">
                    Scroll to the very bottom of the Gmail settings page and
                    click{' '}
                    <strong className="text-gray-300" data-id="element-2502">Save Changes</strong>.
                  </li>
                </ol>
              </div>

              <div className="mt-6 text-center text-xs text-gray-600" data-id="element-2503">
                Social media and contact icons provided by{' '}
                <a href="https://icons8.com" target="_blank" rel="noopener noreferrer" className="text-viridia hover:underline" data-id="element-2504">
                  Icons8
                </a>
                .
              </div>
            </div>

            {/* Opt-In Modal */}
            {showOptIn && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2505">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowOptIn(false)} data-id="element-2506"></div>
                <div className="relative bg-viridia-surface border border-gray-800 rounded-2xl p-8 max-w-md w-full shadow-2xl" data-id="element-2507">
                  <button onClick={() => setShowOptIn(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors" aria-label="Close" data-id="element-2508">
                    <XIcon className="w-5 h-5" data-id="element-2509" />
                  </button>

                  <div className="text-center mb-6" data-id="element-2510">
                    <div className="w-14 h-14 rounded-full bg-viridia/20 flex items-center justify-center mx-auto mb-4" data-id="element-2511">
                      <Wand2Icon className="w-7 h-7 text-viridia" data-id="element-2512" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2513">
                      Your Signature is Ready!
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-2514">
                      Enter your info below to copy your signature. We'll also
                      send you a backup copy via email.
                    </p>
                  </div>

                  <form onSubmit={handleOptInSubmit} className="space-y-4" data-id="element-2515">
                    <div data-id="element-2516">
                      <label htmlFor="optin-name" className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-2517">
                        Full Name *
                      </label>
                      <input type="text" id="optin-name" required value={optInData.name} onChange={e => setOptInData(prev => ({
                    ...prev,
                    name: e.target.value
                  }))} placeholder="John Smith" className="w-full px-4 py-3 bg-viridia-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia transition-colors" data-id="element-2518" />
                    </div>
                    <div data-id="element-2519">
                      <label htmlFor="optin-email" className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-2520">
                        Email Address *
                      </label>
                      <input type="email" id="optin-email" required value={optInData.email} onChange={e => setOptInData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))} placeholder="john@example.com" className="w-full px-4 py-3 bg-viridia-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia transition-colors" data-id="element-2521" />
                    </div>
                    <div data-id="element-2522">
                      <label htmlFor="optin-business" className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-2523">
                        Business Name
                      </label>
                      <input type="text" id="optin-business" value={optInData.businessName} onChange={e => setOptInData(prev => ({
                    ...prev,
                    businessName: e.target.value
                  }))} placeholder="Your Company LLC" className="w-full px-4 py-3 bg-viridia-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia transition-colors" data-id="element-2524" />
                    </div>

                    <div className="flex items-start gap-3" data-id="element-2525">
                      <input type="checkbox" id="optin-consent" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-1 w-4 h-4 rounded border-gray-600 bg-viridia-black text-viridia focus:ring-viridia focus:ring-offset-0 cursor-pointer flex-shrink-0" data-id="element-2526" />
                      <label htmlFor="optin-consent" className="text-xs text-gray-400 cursor-pointer leading-relaxed" data-id="element-2527">
                        I agree to receive marketing and promotional emails from
                        Viridia Analytics LLC, including product updates, tips,
                        and special offers. You can unsubscribe at any time.
                        View our{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-viridia hover:underline" data-id="element-2528">
                          Privacy Policy
                        </a>
                        .
                      </label>
                    </div>

                    {optInError && <p className="text-red-400 text-sm text-center" data-id="element-2529">
                        {optInError}
                      </p>}

                    <button type="submit" disabled={optInSubmitting || !consentChecked} className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-viridia-black bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2530">
                      {optInSubmitting ? <>
                          <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-2531" />{' '}
                          Unlocking...
                        </> : <>
                          <CopyIcon className="w-5 h-5" data-id="element-2532" /> Copy My Signature
                        </>}
                    </button>
                  </form>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300" data-id="element-2533">
          <div className={`flex items-center p-4 rounded-lg shadow-lg border-l-4 ${toast.type === 'success' ? 'bg-viridia-surface border-viridia text-white' : 'bg-red-900/50 border-red-500 text-white'}`} data-id="element-2534">
            {toast.type === 'success' ? <CheckCircleIcon className="w-5 h-5 text-viridia mr-3" data-id="element-2535" /> : <AlertCircleIcon className="w-5 h-5 text-red-500 mr-3" data-id="element-2536" />}
            <div className="font-medium text-sm" data-id="element-2537">{toast.message}</div>
          </div>
        </div>}
    </div>;
}